import React from 'react';

// A small, safe markdown renderer for the Foundation text: headings, paragraphs, bullet and
// numbered lists, tables and **bold**. It builds React elements only (no HTML injection).
// `blocks` maps a {{raci:X}} marker to whatever should be shown there (the RACI tables).

const inline = (text, key) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((p, i) => (p.startsWith('**') && p.endsWith('**')
    ? <strong key={`${key}-${i}`}>{p.slice(2, -2)}</strong>
    : <React.Fragment key={`${key}-${i}`}>{p}</React.Fragment>));
};

const cellsOf = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13, margin: '8px 0 14px' };
const thStyle = { textAlign: 'left', padding: '6px 8px', background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', fontWeight: 600 };
const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' };

const MarkdownView = ({ text, blocks = {} }) => {
  const lines = (text || '').split('\n');
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    const marker = trimmed.match(/^\{\{raci:([A-Z])\}\}$/);
    if (!trimmed) { i += 1; continue; }
    if (marker) {
      out.push(<React.Fragment key={i}>{blocks[marker[1]] || null}</React.Fragment>);
      i += 1;
    } else if (/^#{1,4} /.test(trimmed)) {
      const level = trimmed.match(/^#+/)[0].length;
      const Tag = `h${Math.min(level + 1, 5)}`;
      out.push(<Tag key={i} style={{ margin: '18px 0 8px', color: '#0B1760' }}>{inline(trimmed.replace(/^#+ /, ''), i)}</Tag>);
      i += 1;
    } else if (trimmed.startsWith('|')) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) { rows.push(lines[i]); i += 1; }
      const at = i;
      const head = cellsOf(rows[0]);
      const body = rows.slice(rows[1] && /^\|[\s|:-]+\|?$/.test(rows[1].trim()) ? 2 : 1).map(cellsOf);
      out.push(
        <div key={`t${at}`} style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{head.map((c, j) => <th key={j} style={thStyle}>{inline(c, `h${at}-${j}`)}</th>)}</tr></thead>
            <tbody>{body.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j} style={tdStyle}>{inline(c, `c${at}-${k}-${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
    } else if (/^(-|\d+\.)\s+/.test(trimmed)) {
      const ordered = /^\d+\./.test(trimmed);
      const items = [];
      while (i < lines.length && /^(-|\d+\.)\s+/.test(lines[i].trim()) && (/^\d+\./.test(lines[i].trim()) === ordered)) {
        items.push(lines[i].trim().replace(/^(-|\d+\.)\s+/, ''));
        i += 1;
        while (i < lines.length && !lines[i].trim()) i += 1;
      }
      const List = ordered ? 'ol' : 'ul';
      const at = i;
      out.push(<List key={`l${at}`} style={{ margin: '6px 0 12px', paddingLeft: 22 }}>{items.map((it, k) => <li key={k} style={{ marginBottom: 6 }}>{inline(it, `li${at}-${k}`)}</li>)}</List>);
    } else {
      out.push(<p key={i} style={{ margin: '0 0 10px', lineHeight: 1.6 }}>{inline(trimmed, `p${i}`)}</p>);
      i += 1;
    }
  }
  return <div style={{ fontSize: 14, color: '#0f172a' }}>{out}</div>;
};

export default MarkdownView;
